/* Created by Interactive 63 - https://interactive63.com */
/**
 * Prepares curved motion for export. CSS can only move in straight lines between keyframes, so
 * each curved position segment is replaced by closely spaced points along the curve, and "orient
 * along path" becomes a rotation track sampled the same way. Straight segments are untouched, and
 * the preview, the code exports and the GIF and MP4 renders all compile from the result.
 */
import { isVec2, type Easing, type Keyframe, type Project, type Track } from '../model/types';
import { sampleKeyframeList } from '../timeline/interpolate';
import { isCurvedSegment, travelAngle } from '../timeline/motion-curve';
import type { CompileWarning } from './types';

/** Points per second of animation used to draw a curve as straight steps. */
export const MOTION_STOPS_PER_SECOND = 30;
/** Fewest points used for any curved segment, so short curves stay round. */
const MIN_STOPS_PER_SEGMENT = 8;
/** Decimal places kept in baked positions and angles. */
const BAKED_DECIMALS = 3;

const LINEAR: Easing = { type: 'linear' };

/** Rounds a baked number. */
function round(value: number): number {
  const factor = 10 ** BAKED_DECIMALS;
  return Math.round(value * factor) / factor;
}

/** Whole-millisecond sample times strictly between two keyframes. */
function sampleTimes(start: number, end: number): number[] {
  const span = end - start;
  const wanted = Math.max(
    MIN_STOPS_PER_SEGMENT,
    Math.ceil((span / 1000) * MOTION_STOPS_PER_SECOND),
  );
  const count = Math.min(wanted, span);
  const times = new Set<number>();
  for (let i = 1; i < count; i += 1) times.add(start + Math.round((span * i) / count));
  times.delete(start);
  times.delete(end);
  return [...times].sort((a, b) => a - b);
}

/** A position track's keyframes with every curved segment replaced by points along the curve. */
function bakeCurves(track: Track): Keyframe[] {
  const result: Keyframe[] = [];
  track.keyframes.forEach((from, i) => {
    const to = track.keyframes[i + 1];
    if (!to || !isCurvedSegment(from, to)) {
      result.push(from);
      return;
    }
    result.push({ ...from, easing: LINEAR });
    for (const time of sampleTimes(from.time, to.time)) {
      const value = sampleKeyframeList('vec2', [from, to], time);
      if (!isVec2(value)) continue;
      result.push({
        id: `${from.id}~m${time}`,
        time,
        value: { x: round(value.x), y: round(value.y) },
        easing: LINEAR,
      });
    }
  });
  return result;
}

/** Whether a track has any curved segment. */
function hasCurves(track: Track): boolean {
  return track.keyframes.some((kf, i) => {
    const next = track.keyframes[i + 1];
    return next !== undefined && isCurvedSegment(kf, next);
  });
}

/**
 * A rotation track that turns the element to face its direction of travel, sampled across the
 * position track's span. Angles are unwound so a turn past 180 degrees does not spin back.
 */
function orientTrack(track: Track): Track | null {
  const first = track.keyframes[0];
  const last = track.keyframes[track.keyframes.length - 1];
  if (!first || !last || last.time <= first.time) return null;
  const sample = (time: number) => {
    const value = sampleKeyframeList('vec2', track.keyframes, time);
    return isVec2(value) ? value : undefined;
  };
  const times = [first.time, ...sampleTimes(first.time, last.time), last.time];
  const keyframes: Keyframe[] = [];
  let previous: number | null = null;
  for (const time of times) {
    const raw = travelAngle(sample, time);
    if (raw === null && previous === null) continue;
    let angle: number = raw ?? previous ?? 0;
    if (previous !== null) angle = previous + ((((angle - previous) % 360) + 540) % 360) - 180;
    previous = angle;
    keyframes.push({ id: `${track.id}~o${time}`, time, value: round(angle), easing: LINEAR });
  }
  if (keyframes.length < 2) return null;
  return {
    id: `${track.id}~orient`,
    elementId: track.elementId,
    property: 'rotate',
    keyframes,
    enabled: true,
    ...(track.repeat ? { repeat: track.repeat } : {}),
    ...(track.restartWithScene === undefined ? {} : { restartWithScene: track.restartWithScene }),
  };
}

/**
 * The project as the compiler should see it: curved position segments drawn as straight steps,
 * and orient along path turned into rotation. Returns the same project when nothing is curved or
 * oriented, so the common case costs nothing.
 */
export function bakeMotion(project: Project): { project: Project; warnings: CompileWarning[] } {
  const positionTracks = project.tracks.filter((t) => t.property === 'translate' && t.enabled);
  if (!positionTracks.some((t) => t.orient || hasCurves(t))) return { project, warnings: [] };

  const warnings: CompileWarning[] = [];
  const added: Track[] = [];
  const tracks = project.tracks.map((track) => {
    if (track.property !== 'translate' || !track.enabled) return track;
    if (track.orient) {
      const rotated = project.tracks.some(
        (t) => t.elementId === track.elementId && t.property === 'rotate' && t.enabled,
      );
      const label = project.elements[track.elementId]?.label ?? track.elementId;
      if (rotated) {
        warnings.push({
          code: 'orient-with-rotation',
          message: `Orient along path is ignored for ${label} because it also has a rotation track.`,
          elementId: track.elementId,
        });
      } else {
        const orient = orientTrack(track);
        if (orient) added.push(orient);
      }
    }
    return hasCurves(track) ? { ...track, keyframes: bakeCurves(track) } : track;
  });
  return { project: { ...project, tracks: [...tracks, ...added] }, warnings };
}
